import { Sankey, SankeyDataNode, SankeyLink, SankeyNode } from '@/types/Sankey';
import { CarrierPremiumByWholesaler, WholesalerFilter } from '@/types/Carrier';
import { createSankeyViewNodes, transformMapToShowTop } from '@/utilities/Charts';
import { paletteColors } from '@/utilities/Color';

export function premiumByWholesalerToSankey(data: CarrierPremiumByWholesaler[], carrierName: string, filter: WholesalerFilter): Sankey {
  const ALL_OTHER_WHOLESALERS = 'All Other Wholesalers';
  const ALL_OTHER_COVERAGE_TYPES = 'All Other Coverage Types';

  // filter data
  if (filter === WholesalerFilter.RETAIL) {
    data = data.filter(d => !d.wholesalerGroup);
  } else if (filter === WholesalerFilter.WHOLESALE) {
    data = data.filter(d => d.wholesalerGroup);
  }

  // wholesaler nodes
  const wholesalerMap = data.reduce((acc, d) => {
    const key = (d.wholesalerGroup || 'Retail');
    acc.set(key, (acc.get(key) || 0) + d.policyPremium);
    return acc;
  }, new Map<string, number>());

  const wholesalerSankeyDataNodes: Array<SankeyDataNode> = transformMapToShowTop(20, wholesalerMap);
  const wholesalerSankeyViewNodes: Array<SankeyNode> = createSankeyViewNodes(wholesalerSankeyDataNodes, ALL_OTHER_WHOLESALERS);

  // coverage type nodes
  const coverageTypeMap = data.reduce((acc, d) => {
    const key = (d.coverageType || 'N/A');
    acc.set(key, (acc.get(key) || 0) + d.policyPremium);
    return acc;
  }, new Map<string, number>());

  const coverageTypeSankeyDataNodes: Array<SankeyDataNode> = transformMapToShowTop(10, coverageTypeMap);
  const coverageTypeSankeyViewNodes: Array<SankeyNode> = createSankeyViewNodes(coverageTypeSankeyDataNodes, ALL_OTHER_COVERAGE_TYPES);

  // create wholesaler links for the ungrouped nodes
  let wholesalerLinks = wholesalerSankeyDataNodes.filter(n => !n.grouped).map(w => {
    return {
      from: carrierName,
      to: carrierName === w.label ? `Retail ${w.label}` : w.label,
      value: wholesalerMap.get(w.label)
    };
  }) as SankeyLink[];

  // Carrier Name <--> Wholesalers Links
  wholesalerLinks = [
    ...wholesalerLinks,
    {
      from: carrierName,
      to: ALL_OTHER_WHOLESALERS,
      value: wholesalerSankeyDataNodes.filter(n => n.grouped).reduce((total, node) => total + node.aggregateTotal, 0)
    }
  ];

  // Wholesalers <--> Coverage Type Links
  let coverageTypeLinks = coverageTypeSankeyDataNodes.flatMap(c => {
    return wholesalerSankeyDataNodes.map(w => {
      return {
        from: w.grouped ? ALL_OTHER_WHOLESALERS : w.label,
        to: (() => {
          if (w.label === c.label) {
            return `Retail ${c.label}`;
          }
          if (c.grouped) {
            return ALL_OTHER_COVERAGE_TYPES;
          }
          return c.label;
        })(),
        value: (data.find(d => (d.wholesalerGroup || 'Retail') === w.label && (d.coverageType || 'N/A') === c.label) || { policyPremium: 0 }).policyPremium
      };
    });
  }) as SankeyLink[];

  // consolidate the "All Other Wholesaler Types" <--> "All Other Coverage Types" links
  const filteredCoverageTypeLinks = coverageTypeLinks.filter(l => !(l.from === ALL_OTHER_WHOLESALERS && l.to === ALL_OTHER_COVERAGE_TYPES));
  coverageTypeLinks = [
    ...filteredCoverageTypeLinks,
    {
      from: ALL_OTHER_WHOLESALERS,
      to: ALL_OTHER_COVERAGE_TYPES,
      value: coverageTypeLinks.reduce((total, l) => l.from === ALL_OTHER_WHOLESALERS && l.to === ALL_OTHER_COVERAGE_TYPES ? total + l.value : total, 0)
    }];

  const wholesalerNodes = wholesalerSankeyViewNodes.map((w, i) => ({
    label: w.label,
    showValue: true,
    color: paletteColors[i + 1]
  }));
  const coverageTypeNodes = coverageTypeSankeyViewNodes.map((c, i) => ({
    label: c.label,
    showValue: true,
    color: paletteColors[i + 6]
  }));
  const nodes = wholesalerNodes.concat(coverageTypeNodes).concat({
    label: carrierName,
    showValue: true,
    color: paletteColors[0]
  });
  const links = wholesalerLinks.concat(coverageTypeLinks);
  return { nodes, links } as Sankey;
}